import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { Row, Col, Nav, Tab, Card, Badge, Button } from 'react-bootstrap';
import { MoneyIcon, ClockIcon, MapPinIcon, BookOpenIcon, MailIcon, PhoneIcon, CheckCircleIcon, LandmarkIcon, AwardIcon, ExternalLinkIcon, ChevronLeftIcon, ChevronRightIcon, GraduationCapIcon, InfoIcon, TagIcon } from '../common/icons';

// Enhanced CSS for smooth animations and mobile-first design
const tabContentStyle = {
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: 1,
  transform: 'translateY(0)',
};

const tabNavStyle = {
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
};

// Animation styles for content transitions
const getContentAnimationStyle = (isAnimating) => ({
  ...tabContentStyle,
  opacity: isAnimating ? 0.8 : 1,
  transform: isAnimating ? 'translateY(8px) scale(0.99)' : 'translateY(0) scale(1)',
});

// Mobile-friendly tab navigation styles
const mobileTabStyle = {
  display: 'flex',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  WebkitScrollbar: { display: 'none' },
  gap: '8px',
  padding: '8px 0',
};

const tabButtonStyle = (isActive, primaryColor = '#0d6efd') => ({
  minWidth: '120px',
  padding: '8px 16px',
  borderRadius: '20px',
  border: isActive ? `2px solid ${primaryColor}` : '1px solid #dee2e6',
  backgroundColor: isActive ? `${primaryColor}15` : '#ffffff',
  color: isActive ? primaryColor : '#6c757d',
  fontSize: '14px',
  fontWeight: isActive ? '600' : '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  boxShadow: isActive ? `0 2px 8px ${primaryColor}25` : '0 1px 3px rgba(0, 0, 0, 0.1)',
});

const CourseDetailsCards = ({ selectedCourse, schoolDisplayName, otherSchools, otherSchoolsLoading, activeSchoolTab, setActiveSchoolTab, isOfferedByCurrentTenant = true, onThemeColorChange }) => {
  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousTabIndex, setPreviousTabIndex] = useState(activeSchoolTab);
  const [themeChanged, setThemeChanged] = useState(false); // Track if theme has been changed
  
  // Color extraction state
  const [dominantColors, setDominantColors] = useState(null);
  const [isLoadingColors, setIsLoadingColors] = useState(false);
  const [brandColor, setBrandColor] = useState('#0d6efd'); // bootstrap primary default
  
  // Helper function to determine fallback color when no logo is available
  const getFallbackColor = (schoolData = null) => {
    // For courses not offered by any tenant, use a distinct fallback color
    if (!isOfferedByCurrentTenant && (!otherSchools || otherSchools.length === 0)) {
      return '#6c757d'; // Bootstrap secondary (neutral gray) for no offers
    }
    
    // For tenants with no logos, use a warm fallback color
    if (schoolData && !schoolData.school_logo) {
      return '#fd7e14'; // Bootstrap orange - warm, friendly color for schools without logos
    }
    
    // If we have a logo, don't use fallback (extract colors instead)
    if (schoolData?.school_logo) {
      return null;
    }
    
    // Default fallback for edge cases
    return '#fd7e14'; // Bootstrap orange
  };
  
  // Determine which schools to show based on current tenant offering status
  const allSchools = [];
  
  if (isOfferedByCurrentTenant) {
    // If current tenant offers the course, ONLY show current school (no other schools)
    // Ensure current school has proper logo information
    const currentSchoolWithLogo = {
      ...selectedCourse,
      school_name: schoolDisplayName,
      name: schoolDisplayName,
      is_current_school: true,
      // Try to get logo from selectedCourse first, then construct from school ID if available
      school_logo: selectedCourse?.school_logo || (selectedCourse?.school_id ? `${api?.defaults?.baseURL || ''}/api/school-logo/${selectedCourse.school_id}/` : null)
    };
    allSchools.push(currentSchoolWithLogo);
  } else {
    // If current tenant doesn't offer the course, show other schools that do
    const filteredOtherSchools = otherSchools.filter((s) =>
      (s.school_name || s.name || "").toLowerCase() !== (schoolDisplayName || "").toLowerCase(),
    );
    
    allSchools.push(...filteredOtherSchools.map(school => ({
      ...school,
      is_current_school: false
    })));
  }
  
  const hasMultipleSchools = allSchools.length > 1;
  

  
  // Ensure activeSchoolTab is within bounds
  const safeActiveTab = Math.min(activeSchoolTab, allSchools.length - 1);
  
  // Get the currently selected school data
  const currentSchoolData = allSchools[safeActiveTab] || allSchools[0] || selectedCourse;
  

  
  // Handle tab changes with animation
  const handleTabChange = (newTabIndex) => {
    if (newTabIndex !== safeActiveTab && newTabIndex < allSchools.length) {
      setIsAnimating(true);
      setPreviousTabIndex(safeActiveTab);
      
      // Start fade out
      setTimeout(() => {
        setActiveSchoolTab(newTabIndex);
        
        // Start fade in after a brief delay
        setTimeout(() => {
          setIsAnimating(false);
        }, 100);
      }, 150);
      
      // Immediately trigger color extraction for the new school
      const newSchoolData = allSchools[newTabIndex];
      if (newSchoolData) {
        console.log('🎨 Tab changed, extracting colors for:', {
          school: newSchoolData.school_name || newSchoolData.name,
          logo: newSchoolData.school_logo,
          id: newSchoolData.school_id || newSchoolData.id,
          tabIndex: newTabIndex
        });
        
        // Reset colors while fetching new ones
        setDominantColors(null);
        setThemeChanged(false);
        
        if (newSchoolData.school_logo) {
          console.log('🎨 Logo found, extracting colors...');
          extractColorsFromLogo(newSchoolData);
        } else {
          console.log('🎨 No logo found, applying fallback color...');
          // No logo, apply fallback color immediately
          const fallbackColor = getFallbackColor(newSchoolData);
          if (fallbackColor && typeof onThemeColorChange === 'function') {
            console.log('🎨 No logo available, applying fallback color:', fallbackColor);
            onThemeColorChange(fallbackColor);
            setThemeChanged(true);
          }
        }
      } else {
        console.warn('❌ No school data found for tab index:', newTabIndex);
      }
    }
  };
  
  // Navigation helpers for mobile
  const goToPrevious = () => {
    const newIndex = safeActiveTab > 0 ? safeActiveTab - 1 : allSchools.length - 1;
    handleTabChange(newIndex);
  };
  
  const goToNext = () => {
    const newIndex = safeActiveTab < allSchools.length - 1 ? safeActiveTab + 1 : 0;
    handleTabChange(newIndex);
  };
  
  // NEW: Simplified color extraction function
  const extractColorsFromLogo = async (schoolData) => {
    if (!schoolData?.school_logo) {
      console.log('🎨 No logo URL, skipping color extraction');
      return;
    }
    
    // Get school ID from the logo URL
    const logoUrl = schoolData.school_logo;
    const idMatch = logoUrl.match(/\/api\/school-logo\/(\d+)\/?/);
    
    if (!idMatch) {
      console.log('🎨 Could not extract school ID from logo URL:', logoUrl);
      return;
    }
    
    const schoolId = idMatch[1];
    console.log('🎨 Extracting colors for school ID:', schoolId);
    
    try {
      setIsLoadingColors(true);
      
      // Use the JSON endpoint directly
      const apiBaseUrl = api?.defaults?.baseURL || 'http://localhost:8000';
      const colorsUrl = `${apiBaseUrl}/api/school-logo-colors/${schoolId}/`;
      
      console.log('🎯 Fetching colors from:', colorsUrl);
      
      const response = await fetch(colorsUrl);
      console.log('🎯 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🎯 Response data:', data);
        
        if (data && Array.isArray(data.colors) && data.colors.length > 0) {
          console.log('🎨 Successfully extracted colors:', data.colors);
          setDominantColors(data.colors);
          
          // Immediately apply the first color to the header
          if (typeof onThemeColorChange === 'function') {
            console.log('🎨 Applying color to header:', data.colors[0].hex);
            onThemeColorChange(data.colors[0].hex);
          }
        } else {
          console.log('🎨 No colors found in response');
          setDominantColors(null);
        }
      } else {
        console.log('🎨 Failed to fetch colors, status:', response.status);
        setDominantColors(null);
      }
      




      
    } catch (error) {
      console.error('🎨 Error extracting colors:', error);
      setDominantColors(null);
    } finally {
      setIsLoadingColors(false);
    }
  };

  // Reset animation when data changes
  useEffect(() => {
    setIsAnimating(false);
  }, [currentSchoolData]);
  
  // Extract colors when school selection changes (guard against duplicate fetches)
  const lastColorKeyRef = useRef(null);
  useEffect(() => {
    if (!currentSchoolData) return;
    
    // Create a more comprehensive color key that includes the school data
    const colorKey = `${currentSchoolData.school_logo || 'no-logo'}-${currentSchoolData.school_id || currentSchoolData.id || 'no-id'}-${safeActiveTab}`;
    
    console.log('🎨 Color extraction triggered:', {
      school: currentSchoolData.school_name || currentSchoolData.name,
      logo: currentSchoolData.school_logo,
      id: currentSchoolData.school_id || currentSchoolData.id,
      tab: safeActiveTab,
      colorKey,
      lastKey: lastColorKeyRef.current
    });
    
    if (colorKey === lastColorKeyRef.current) {
      console.log('🎨 Skipping duplicate color extraction');
      return; // skip duplicate
    }
    
    lastColorKeyRef.current = colorKey;

    // Reset to neutral while fetching (avoid showing previous school's color)
    setDominantColors(null);
    setBrandColor('#0d6efd');
    // Reset theme changed flag so new colors can be applied
    setThemeChanged(false);
    
    if (currentSchoolData.school_logo) {
      console.log('🎨 Extracting colors from logo for:', currentSchoolData.school_name || currentSchoolData.name);
      extractColorsFromLogo(currentSchoolData);
    } else {
      console.log('🎨 No logo available for:', currentSchoolData.school_name || currentSchoolData.name);
      // Apply fallback color immediately for schools without logos
      const fallbackColor = getFallbackColor(currentSchoolData);
      if (fallbackColor && typeof onThemeColorChange === 'function') {
        console.log('🎨 Applying fallback color for school without logo:', fallbackColor);
        onThemeColorChange(fallbackColor);
        setThemeChanged(true);
      }
    }
  }, [currentSchoolData?.school_logo, currentSchoolData?.school_id, currentSchoolData?.id, safeActiveTab]);
  
  // Auto-adjust tab if out of bounds
  useEffect(() => {
    if (safeActiveTab !== activeSchoolTab) {
      setActiveSchoolTab(safeActiveTab);
    }
  }, [safeActiveTab, activeSchoolTab, setActiveSchoolTab]);
  
  // Apply extracted colors immediately when they become available
  useEffect(() => {
    if (dominantColors && dominantColors.length > 0 && !isLoadingColors && typeof onThemeColorChange === 'function') {
      console.log('🎨 Colors extracted, applying to header immediately:', dominantColors[0]?.hex);
      console.log('🎨 onThemeColorChange function exists:', typeof onThemeColorChange === 'function');
      console.log('🎨 Current themeChanged state:', themeChanged);
      
      // Always apply logo colors immediately, regardless of themeChanged state
      onThemeColorChange(dominantColors[0].hex);
      setThemeChanged(true);
      
      console.log('🎨 Color applied successfully');
    }
  }, [dominantColors, isLoadingColors, onThemeColorChange]);
  
  // Reset theme changed flag when course changes
  useEffect(() => {
    console.log('🎨 Course changed, resetting themeChanged flag');
    setThemeChanged(false);
  }, [selectedCourse?.course_name]);
  
  // Apply initial colors when component mounts or course changes
  useEffect(() => {
    console.log('🎨 Initial color application check:', {
      hasOnThemeColorChange: typeof onThemeColorChange === 'function',
      themeChanged,
      hasDominantColors: dominantColors && dominantColors.length > 0,
      dominantColorHex: dominantColors?.[0]?.hex,
      isLoadingColors,
      currentSchoolData: currentSchoolData?.school_name || currentSchoolData?.name
    });
    
    if (typeof onThemeColorChange === 'function' && !themeChanged) {
      // If we have logo colors, use them
      if (dominantColors && dominantColors.length > 0) {
        console.log('🎨 Applying initial logo colors:', dominantColors[0]?.hex);
        onThemeColorChange(dominantColors[0].hex);
        setThemeChanged(true);
      } else if (!isLoadingColors) {
        // No logo colors yet, check if we should use fallback
        const fallbackColor = getFallbackColor(currentSchoolData);
        if (fallbackColor) {
          console.log('🎨 Applying initial fallback color:', fallbackColor);
          onThemeColorChange(fallbackColor);
          setThemeChanged(true);
        }
      }
    }
  }, [selectedCourse?.course_name, dominantColors, isLoadingColors, onThemeColorChange, themeChanged, getFallbackColor, currentSchoolData]);
  
  console.log("🎨 CourseDetailsCards - Received data:", {
    selectedCourse,
    hasMultipleSchools,
    allSchools: allSchools.length,
    currentSchoolData,
    activeSchoolTab: safeActiveTab,
    isOfferedByCurrentTenant
  });
  

  
  // Helper functions for current school data
  const getSchoolData = (school = currentSchoolData) => {
    
    const currency = school.currency || "PHP";
    const asInt = (v) =>
      v === null || v === undefined || v === ""
        ? null
        : Number.isNaN(Number(v))
          ? null
          : Number(v);
    const min = asInt(school.tuition_min);
    const max = asInt(school.tuition_max);
    const formatCurrency = (n) =>
      typeof n === "number" ? n.toLocaleString() : "—";
    const tuitionText =
      min !== null || max !== null
        ? `${currency === "PHP" ? "₱" : ""}${formatCurrency(min)} – ${
            currency === "PHP" ? "₱" : ""
          }${formatCurrency(max)}`
        : "Contact school";
    const durationYears = asInt(school.duration_years);
    const durationText =
      durationYears !== null && durationYears > 0
        ? `${durationYears} year${durationYears > 1 ? "s" : ""}`
        : "Contact school";
    const modality = school.modality || "Contact school";
    const location =
      school.campus_location ||
      school.school_address ||
      "Contact school";
    const scholarship = school.scholarship_available
      ? "Yes"
      : "Contact school";
    const appLink = school.application_link || null;
    const contactEmail = school.contact_email || null;
    const contactPhone = school.contact_phone || null;
    
    return {
      currency, min, max, formatCurrency, tuitionText, durationText, 
      modality, location, scholarship, appLink, contactEmail, contactPhone
    };
  };
  
  const { tuitionText, durationText, modality, location, scholarship, appLink, contactEmail, contactPhone } = getSchoolData();
  
  // Generate dynamic styles based on extracted colors
  const getDynamicStyles = () => {
    // If we have extracted logo colors, use them
    if (dominantColors && dominantColors.length > 0) {
      const primaryColor = dominantColors[0]?.hex || brandColor;
      const secondaryColor = dominantColors[1]?.hex || primaryColor;
      const accentColor = dominantColors[2]?.hex || primaryColor;
      
      const gradient = `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}0f 100%)`;
      
      return {
        primaryColor,
        secondaryColor,
        accentColor,
        gradient
      };
    }
    
    // No logo colors available, check if we should use fallback
    // Note: We can't call getFallbackColor here because currentSchoolData isn't available yet
    // Use neutral colors until we can determine the proper fallback
    return {
      primaryColor: '#6c757d', // Bootstrap secondary (neutral gray)
      secondaryColor: '#6c757d',
      accentColor: '#6c757d',
      gradient: `linear-gradient(135deg, #6c757d22 0%, #6c757d0f 100%)`
    };
  };
  
    const dynamicStyles = getDynamicStyles();
    // If the primary color is red-ish, also tint key headings/bullets accordingly


  // Dynamic spacing based on course title length
  const courseTitleText = (selectedCourse?.course_name || selectedCourse?.name || '').trim();
  const titleLength = courseTitleText.length;
  // More breathing room for very long titles
  const headerRowMarginBottom = titleLength >= 34 ? 12 : titleLength >= 30 ? 10 : titleLength >= 24 ? 6 : 8; // px
  const availableAtMarginTop = titleLength >= 34 ? 0 : titleLength >= 30 ? -2 : titleLength >= 24 ? -4 : -2; // px
  
  // Debug popup removed per request
  const debugColors = null;
  
  return (
    <div className="course-details-container">
      {debugColors}
      {/* Course Header */}
      <Card className="mb-4 border-0 shadow-sm" style={{ 
        background: dynamicStyles.gradient,
        border: `1px solid ${dynamicStyles.primaryColor}20`,
        borderRadius: '14px'
      }}>
        <Card.Body className="text-center p-4">
          <div className="d-flex align-items-center justify-content-center gap-2" style={{ marginBottom: headerRowMarginBottom }}>
            {/* Hide logo on mobile, show on desktop */}
            <div className="d-none d-md-block">
              {currentSchoolData?.school_logo ? (
                <img
                  src={currentSchoolData.school_logo}
                  alt={`${currentSchoolData.school_name || currentSchoolData.name} logo`}
                  style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '6px' }}
                  onError={(e) => { 
                    console.warn('❌ Main logo failed to load:', currentSchoolData.school_logo);
                    e.currentTarget.style.display = 'none'; 
                  }}
                />
              ) : (
                /* Show school logo placeholder when no logo is available */
                <div className="d-flex align-items-center justify-content-center" style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '6px',
                  background: '#f8f9fa',
                  border: '2px solid #dee2e6',
                  color: '#6c757d'
                }}>
                  <LandmarkIcon size={24} />
                </div>
              )}
            </div>
            <div className="d-flex align-items-center">
              <h3 className="mb-0 fw-bold" style={{ 
                fontSize: 'clamp(18px, 4vw, 24px)',
                color: dynamicStyles.primaryColor
              }}>
                {selectedCourse.course_name || selectedCourse.name}
              </h3>
            </div>
            {isLoadingColors && (
              <div className="ms-2">
                <div className="spinner-border spinner-border-sm" style={{ color: dynamicStyles.primaryColor }} role="status">
                  <span className="visually-hidden">Loading colors...</span>
                </div>
              </div>
            )}
          </div>

          {/* RIASEC removed as requested */}
          
          {/* School Navigation - Enhanced for multiple schools */}
          {hasMultipleSchools ? (
            <div className="school-navigation">
              {/* Mobile-friendly tab navigation */}
              <div className="d-block d-md-none">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={goToPrevious}
                    className="rounded-circle p-2"
                    style={{ width: '36px', height: '36px' }}
                  >
                    <ChevronLeftIcon size={12} />
                  </Button>
                  
                  <div className="text-center flex-grow-1 mx-3">
                    {/* Center logo - bigger in mobile view */}
                    <div className="d-flex justify-content-center mb-2">
                      {currentSchoolData.school_logo ? (
                        <img 
                          src={currentSchoolData.school_logo} 
                          alt={`${currentSchoolData.school_name || currentSchoolData.name} logo`}
                          style={{ 
                            width: '48px', 
                            height: '48px', 
                            objectFit: 'contain',
                            borderRadius: '6px'
                          }}
                          onError={(e) => {
                            console.warn('❌ Mobile logo failed to load:', currentSchoolData.school_logo);
                            // Hide the image if it fails to load, but don't show fallback icon
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : null}
                    </div>
                    <div className="d-flex justify-content-center mb-1">
                      {currentSchoolData.is_current_school ? (
                        <Badge bg="success" className="d-flex align-items-center gap-1">
                          <CheckCircleIcon size={10} />
                          {schoolDisplayName}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="fw-bold text-dark mt-1" style={{ fontSize: '14px' }}>
                      {currentSchoolData.school_name || currentSchoolData.name}
                    </div>
                    <small className="text-muted">
                      {safeActiveTab + 1} of {allSchools.length} schools
                    </small>
                  </div>
                  
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={goToNext}
                    className="rounded-circle p-2"
                    style={{ width: '36px', height: '36px' }}
                  >
                    <ChevronRightIcon size={12} />
                  </Button>
                </div>
              </div>
              
              {/* Desktop tab navigation */}
              <div className="d-none d-md-block">
                <div style={mobileTabStyle} className="justify-content-center">
                  {allSchools.map((school, index) => (
                    <div
                      key={index}
                      style={tabButtonStyle(index === safeActiveTab, dynamicStyles.primaryColor)}
                      onClick={() => handleTabChange(index)}
                      className="d-flex align-items-center gap-2"
                    >
                      {school.school_logo ? (
                        <img 
                          src={school.school_logo} 
                          alt={`${school.school_name || school.name} logo`}
                          style={{ 
                            width: '22px', 
                            height: '22px', 
                            objectFit: 'contain',
                            borderRadius: '2px'
                          }}
                          onError={(e) => {
                            console.warn('❌ Desktop tab logo failed to load:', school.school_logo);
                            // Hide the image if it fails to load
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : null}
                      {school.is_current_school ? (
                        <CheckCircleIcon className="text-success" size={12} />
                      ) : null}
                      <span className="text-truncate" style={{ maxWidth: '150px' }}>
                        {school.school_name || school.name}
                      </span>
                      {school.is_current_school && (
                        <Badge bg="success" className="ms-1" style={{ fontSize: '10px' }}>
                          {schoolDisplayName}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-center gap-2" style={{ marginTop: availableAtMarginTop }}>
              {isOfferedByCurrentTenant ? (
                <Badge bg="primary" className="d-flex align-items-center gap-1">
                  <CheckCircleIcon size={10} />
                  Available at {schoolDisplayName}
                </Badge>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <LandmarkIcon className="text-primary" />
                  <span className="text-dark fw-semibold">
                    {currentSchoolData.school_name || currentSchoolData.name || "School Information"}
                  </span>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
      
      {/* Course Description/Explanation Section */}
      {(selectedCourse.description || selectedCourse.riasec_area) && (
        <Card className="mb-4 border-0 shadow-sm" style={{ 
          background: '#ffffff',
          border: `1px solid ${dynamicStyles.primaryColor}22`,
          borderRadius: '14px'
        }}>
          <Card.Body className="p-4" style={{ padding: '22px 24px' }}>
            {selectedCourse.description && (
              <div className="mb-3">
                <h5 className="fw-bold mb-3 d-flex align-items-center" style={{ color: dynamicStyles.primaryColor }}>
                  <InfoIcon className="me-2" size={18} style={{ color: dynamicStyles.primaryColor }} />
                  Course Description
                </h5>
                <p className="text-dark" style={{ lineHeight: '1.8', fontSize: '15px', margin: 0 }}>
                  {selectedCourse.description}
                </p>
              </div>
            )}
            {/* RIASEC removed as requested */}
          </Card.Body>
        </Card>
      )}

      {/* Course Details Grid */}
      <div style={getContentAnimationStyle(isAnimating)} className="position-relative">
        {isAnimating && (
          <div className="position-absolute top-50 start-50 translate-middle" style={{zIndex: 10}}>
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        
        <Row className="g-3">
          {/* Tuition and Duration */}
          <Col xs={6} md={6}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center p-3">
                <div className="d-flex align-items-center justify-content-center text-success mb-2">
                  <MoneyIcon className="me-2" size={16} />
                  <small className="fw-semibold">Tuition Range</small>
                </div>
                <div className="text-success fw-bold" style={{ fontSize: 'clamp(14px, 3vw, 18px)' }}>
                  {tuitionText}
                  {(getSchoolData().min !== null || getSchoolData().max !== null) && (
                    <div className="text-muted fw-normal mt-1" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>
                      per semester
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col xs={6} md={6}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center p-3">
                <div className="d-flex align-items-center justify-content-center text-info mb-2">
                  <ClockIcon className="me-2" size={16} />
                  <small className="fw-semibold">Duration</small>
                </div>
                <div className="text-dark fw-bold" style={{ fontSize: 'clamp(14px, 3vw, 18px)' }}>
                  {durationText}
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Class Format and Scholarship */}
          <Col xs={6} md={6}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center p-3">
                <div className="d-flex align-items-center justify-content-center text-primary mb-2">
                  <BookOpenIcon className="me-2" size={16} />
                  <small className="fw-semibold">Class Format</small>
                </div>
                <div className="text-dark fw-bold" style={{ fontSize: 'clamp(13px, 2.5vw, 16px)' }}>
                  {modality}
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col xs={6} md={6}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center p-3">
                <div className="d-flex align-items-center justify-content-center text-warning mb-2">
                  <AwardIcon className="me-2" size={16} />
                  <small className="fw-semibold">Scholarship</small>
                </div>
                <div className={`fw-bold ${scholarship === 'Yes' ? 'text-success' : 'text-muted'}`} style={{ fontSize: 'clamp(13px, 2.5vw, 16px)' }}>
                  {scholarship === 'Yes' ? 'Available' : scholarship}
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Campus Location */}
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center p-3">
                <div className="d-flex align-items-center justify-content-center text-warning mb-2">
                  <MapPinIcon className="me-2" size={16} />
                  <small className="fw-semibold">Campus Location</small>
                </div>
                <div className="text-dark fw-bold" style={{ fontSize: 'clamp(13px, 2.5vw, 15px)', wordBreak: 'break-word' }}>
                  {location}
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Contact Information */}
          <Col xs={6} md={6}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center p-3">
                <div className="d-flex align-items-center justify-content-center text-primary mb-2">
                  <MailIcon className="me-2" size={14} />
                  <small className="fw-semibold">Email</small>
                </div>
                <div className="text-dark fw-bold" style={{ fontSize: 'clamp(10px, 2vw, 13px)', wordBreak: 'break-all' }}>
                  {contactEmail ? (
                    <a href={`mailto:${contactEmail}`} className="text-decoration-none text-primary">
                      {contactEmail}
                    </a>
                  ) : (
                    <span className="text-muted">Not available</span>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col xs={6} md={6}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center p-3">
                <div className="d-flex align-items-center justify-content-center text-secondary mb-2">
                  <PhoneIcon className="me-2" size={14} />
                  <small className="fw-semibold">Phone</small>
                </div>
                <div className="text-dark fw-bold" style={{ fontSize: 'clamp(11px, 2vw, 14px)' }}>
                  {contactPhone ? (
                    <a href={`tel:${contactPhone}`} className="text-decoration-none text-primary">
                      {contactPhone}
                    </a>
                  ) : (
                    <span className="text-muted">Not available</span>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Application */}
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center p-4">
                <div className="d-flex align-items-center justify-content-center text-success mb-3">
                  <ExternalLinkIcon className="me-2" size={16} />
                  <small className="fw-semibold">Application</small>
                </div>
                <div>
                  {appLink ? (
                    <Button
                      href={appLink}
                      target="_blank"
                      rel="noreferrer"
                      variant="success"
                      className="d-flex align-items-center gap-2 mx-auto"
                      style={{ fontSize: 'clamp(13px, 2.5vw, 16px)', maxWidth: '200px' }}
                    >
                      <ExternalLinkIcon size={14} />
                      Apply Now
                    </Button>
                  ) : (
                    <span className="fw-bold text-muted" style={{ fontSize: 'clamp(13px, 2.5vw, 16px)' }}>
                      Contact school for application details
                    </span>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CourseDetailsCards;